<template>
    <div>
        <div class="h2">
            Listing des fiches de créations
        </div>
        <hr>

        <button
            v-can:exec-action="'dossiers.import_excel'"
            class="float-right btn btn-primary mr-2 mb-2"
            :title="'Ce bouton permet d\'importer des dossiers depuis un fichier Excel (format du fichier Excel à respecter).'"
            @click="showModalImportationDossiersExcel = true"
        >
            Importer dossiers Excel
        </button>

        <DossierCreate class="float-right" />

        <div class="clearfix" />

        <form
            class="mt-3 py-4 shadow px-3"
            @submit.prevent="search"
        >
            <div class="h4">
                Recherche de dossiers en cours de création
            </div>

            <div class="form-row mt-4">
                <div class="col-2">
                    <base-input
                        v-model="params.cegid"
                        :errors="errors.cegid"
                    >
                        Dossier (CEGID)
                    </base-input>
                </div>

                <div class="col-2">
                    <base-input
                        v-model="params.client"
                        :errors="errors.client"
                    >
                        Client
                    </base-input>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.regime_tva"
                        :errors="errors.regime_tva"
                        :options="optionsRegimesTVA"
                        multiple
                    >
                        Régime TVA
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.collaborateurs"
                        :options="addEmptyOption(optionsCollaborateurs)"
                        multiple
                    >
                        Collaborateur
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.equipes"
                        :options="optionsEquipes"
                        multiple
                    >
                        Équipe
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.cabinets"
                        :options="optionsCabinets"
                        multiple
                    >
                        Cabinet
                    </base-select>
                </div>
            </div>

            <div class="form-row">
                <div class="col-2">
                    <base-select
                        v-model="params.creationStatuts"
                        :options="optionsCreationStatuts.filter(option => option.value !== $store.state.creationStatuts.statutSupervise)"
                        multiple
                    >
                        Statut de création
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.actif"
                        :options="optionsOuiNon"
                    >
                        Actif
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.typeIndivisions"
                        :errors="errors.typeIndivisions"
                        :options="optionsTypesIndivisions"
                        multiple
                    >
                        Indivision/Société
                    </base-select>
                </div>

                <div class="col-2">
                    <base-select
                        v-model="params.type"
                        :errors="errors.type"
                        :options="optionsTypeDossier"
                        multiple
                    >
                        Type de dossier
                    </base-select>
                </div>
            </div>

            <div class="form-row">
                <div class="col-2">
                    <base-select
                        v-model="params.logiciel_compta"
                        :options="optionsLogicielCompta"
                        multiple
                    >
                        Logiciel de comptabilité
                    </base-select>
                </div>
                <div class="col-2">
                    <base-select
                        v-model="params.pack"
                        :options="optionsPack"
                        multiple
                    >
                        Pack
                    </base-select>
                </div>
            </div>

            <div class="form-row">
                <div class="col-2">
                    <base-input
                        v-model="params.id_externe_client"
                        type="text"
                    >
                        Identifiant externe client
                    </base-input>
                </div>
                <div class="col-2">
                    <base-input
                        v-model="params.id_externe_lot"
                        type="text"
                    >
                        Identifiant externe lot
                    </base-input>
                </div>
                <div class="col-2">
                    <base-input
                        v-model="params.email"
                        :errors="errors.email"
                    >
                        Email
                    </base-input>
                </div>
            </div>

            <button
                type="submit"
                class="btn btn-success float-right mt-3 px-4"
            >
                Rechercher
            </button>

            <button
                class="btn btn-info float-right mt-3 px-4 mr-2"
                @click.prevent="reinit"
            >
                Réinitialiser
            </button>
            <span class="clearfix" />
        </form>

        <base-table
            :items="items"
            :meta="meta"
            :params="exParams"
            :loading="loading"
            :head="head"
            name="dossier"
            class="mt-5"
            @orderBy="orderBy"
            @change-page="goToPage"
            @paginate="paginate"
            @export="exportExcel"
        >
            <tr
                v-for="dossier of items"
                :key="dossier.id"
            >
                <td>{{ dossier.cegid }}</td>
                <td>
                    <template v-if="dossier.type === 'foncier'">
                        Foncier
                    </template>

                    <template v-if="dossier.client.is_indivision">
                        {{ dossier.client.type_indivision }} {{ dossier.client.libelle_indivision }}
                    </template>
                    <template v-else>
                        {{ dossier.client.nom }} {{ dossier.client.prenom }}
                    </template>
                </td>
                <td>{{ dossier.client.telephone }}</td>
                <td>{{ dossier.client.email }}</td>
                <td>{{ dossier.date_debut_activite | frenchDate }}</td>
                <td>{{ dossier.is_cga ? 'Oui' : 'Non' }}</td>
                <td>{{ dossier.regime_tva }}</td>
                <td>{{ dossier.date_prise_effet_changement_regime | frenchDate }}</td>
                <td>{{ dossier.cabinet !== null ? dossier.cabinet.nom : '' }}</td>
                <td>{{ dossier.equipe !== null ? dossier.equipe.nom : '' }}</td>
                <td>{{ dossier.updated_at_global | frenchDateTime }}</td>
                <td>{{ dossier.creation_statut.nom }}</td>
                <td class="text-center">
                    <router-link
                        v-can:read-model="'dossier'"
                        :to="{name: 'dossier', params: {id: dossier.id}}"
                        class="btn btn-primary btn-sm"
                        title="Voir"
                    >
                        <i class="fa fa-glasses" />
                    </router-link>
                </td>
            </tr>
        </base-table>

        <ImportationDossiersExcelModal
            v-if="showModalImportationDossiersExcel"
            @close="showModalImportationDossiersExcel = false"
        />
    </div>
</template>

<script>
    import ImportationDossiersExcelModal from "../modals/ImportationDossiersExcelModal"
    import BaseInput from "../templates/BaseInput"
    import DossierSearch from "../../mixins/DossierSearch"
    import BaseTable from "../templates/BaseTable"
    import DossierCreate from "../actions/DossierCreate"
    import {mapGetters} from 'vuex'
    import BaseSelect from "../templates/BaseSelect"

    export default {
        name: "FichesCreationsList",
        components: {BaseSelect, DossierCreate, BaseTable, BaseInput, ImportationDossiersExcelModal},

        mixins: [DossierSearch],

        data: function () {
            return {
                params: this.initParams(),

                showModalImportationDossiersExcel: false,

                head: {
                    cegid: "CEGID",
                    client: "Client",
                    telephone: "Téléphone",
                    email: "Email",
                    date_debut_activite: "Date début d'activité",
                    is_cga: "CGA",
                    regime_tva: "Régime TVA",
                    date_prise_effet_changement_regime: "Date ch. régime",
                    cabinet: "Cabinet",
                    equipe: "Équipe",
                    updated_at_global: "Dernière modification",
                    creationStatut: "Statut de création"
                },

                optionsOuiNon: [
                    {value: '', label: 'Tous'},
                    {value: '1', label: 'Oui'},
                    {value: '0', label: 'Non'},
                ],
            }
        },

        computed: {
            ...mapGetters([
                'optionsCollaborateurs',
                'optionsEquipes',
                'optionsLogicielCompta',
                'optionsPack',
                'optionsCabinets',
                'optionsRegimesTVA',
                'optionsCreationStatuts',
                'optionsTypeDossier',
                'optionsTypesIndivisions',
                'me',
            ])
        },

        methods: {
            /**
             * Réinitialise les paramètres
             */
            reinit: function () {
                this.params = this.initParams()
            },

            /**
             * Initialise les paramètres
             */
            initParams: function () {
                return {
                    paginate: 15,
                    cree: 0,
                    actif: '1',
                    clientSorti: 0,
                    dossierSorti: 0,
                }
            },

            /**
             * Ajout une option vide
             * @param options Array
             * @return Array
             */
            addEmptyOption: function (options) {
                return [{value: '', label: 'Non renseigné'}].concat(options)
            },
        }
    }
</script>
